import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MdAdd,
  MdCheck,
  MdClose,
  MdDelete,
  MdEdit,
  MdSchool,
  MdPeople,
  MdCalendarMonth,
  MdSignalCellular1Bar,
  MdShowChart,
} from "react-icons/md";
import { db } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";

// ─── Module metadata ───────────────────────────────────────────────────────────

const MODULES = [
  { key: "academyActive",   label: "Academia",  icon: <MdSchool /> },
  { key: "communityActive", label: "Comunidad", icon: <MdPeople /> },
  { key: "schedulesActive", label: "Agenda",    icon: <MdCalendarMonth /> },
  { key: "signalsActive",   label: "Señales",   icon: <MdSignalCellular1Bar /> },
  { key: "tradingActive",   label: "Trading",   icon: <MdShowChart /> },
];

const BADGE_OPTIONS = ["", "POPULAR", "ELITE"];

const BADGE_COLOR = {
  POPULAR: { bg: "rgba(151,71,255,0.15)", border: "rgba(151,71,255,0.5)", text: Colors.primaryLight },
  ELITE:   { bg: "rgba(255,159,10,0.12)", border: "rgba(255,159,10,0.5)", text: Colors.warning },
};

const drawerPaper = { sx: { top: "72px", height: "calc(100% - 72px)", backgroundColor: Colors.bgSecondary, borderLeft: `1px solid ${Colors.borderSecondary}` } };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: Colors.bgTertiary,
    color: Colors.textPrimary,
    "& fieldset": { borderColor: Colors.borderSecondary },
    "&:hover fieldset": { borderColor: Colors.primary },
    "&.Mui-focused fieldset": { borderColor: Colors.primary },
  },
  "& .MuiInputLabel-root": { color: Colors.textSecondary },
  "& .MuiFormHelperText-root": { color: Colors.textMuted },
};

// ─── Landing Plans ─────────────────────────────────────────────────────────────

function LandingPlansTab() {
  const [plans,      setPlans]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft,      setDraft]      = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState("");
  const [deleteId,   setDeleteId]   = useState(null);

  useEffect(() => {
    let active = true;
    const unsub = db.collection("plans").orderBy("order", "asc").onSnapshot(
      (snap) => {
        if (!active) return;
        setPlans(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => { if (!active) return; setError("No se pudo cargar la colección `plans`."); setLoading(false); }
    );
    return () => { active = false; unsub(); };
  }, []);

  const openEditor = (plan) => {
    setSaveError("");
    setDraft({
      id:           plan?.id           || "",
      name:         plan?.name         || "",
      price:        plan?.price        ?? "",
      renewalPrice: plan?.renewalPrice ?? "",
      badge:        plan?.badge        || "",
      order:        plan?.order        ?? plans.length,
      isActive:     plan?.isActive     !== false,
      features:     Array.isArray(plan?.features) ? plan.features.join("\n") : "",
      isNew:        !plan?.id,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => { if (saving) return; setDrawerOpen(false); setDraft(null); };
  const handleChange = (key, value) => setDraft((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError("");
    const id = draft.id || draft.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (!id) { setSaveError("El nombre es requerido."); setSaving(false); return; }
    try {
      await db.collection("plans").doc(id).set({
        name:         draft.name.trim(),
        price:        Number(draft.price)        || 0,
        renewalPrice: Number(draft.renewalPrice) || 0,
        badge:        draft.badge || null,
        order:        Number(draft.order) || 0,
        isActive:     Boolean(draft.isActive),
        features:     draft.features.split("\n").map((s) => s.trim()).filter(Boolean),
      }, { merge: true });
      setDrawerOpen(false);
      setDraft(null);
    } catch {
      setSaveError("No se pudo guardar. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try { await db.collection("plans").doc(deleteId).delete(); } catch { /* silent */ }
    setDeleteId(null);
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: Colors.primary }} /></Box>;
  if (error)   return <Typography color="error">{error}</Typography>;

  return (
    <>
      {/* Action bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <Button
          variant="contained"
          startIcon={<MdAdd />}
          onClick={() => openEditor(null)}
          sx={{ textTransform: "none", borderRadius: 2, backgroundColor: Colors.primary, color: "#fff", fontWeight: 600, "&:hover": { backgroundColor: Colors.primaryDark } }}
        >
          Nuevo plan
        </Button>
      </Box>

      {/* Plan cards grid */}
      {plans.length === 0 ? (
        <Paper sx={{ p: 6, borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, textAlign: "center" }}>
          <Typography sx={{ color: Colors.textSecondary }}>Sin planes. Crea el primero con el botón de arriba.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {plans.map((plan) => {
            const badge = BADGE_COLOR[plan.badge];
            const features = Array.isArray(plan.features) ? plan.features : [];
            return (
              <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    backgroundColor: Colors.bgSecondary,
                    border: `1px solid ${plan.isActive ? Colors.borderSecondary : Colors.error + "55"}`,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    opacity: plan.isActive ? 1 : 0.65,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: `0 12px 40px rgba(0,0,0,0.4)` },
                  }}
                >
                  {/* Top accent bar */}
                  <Box sx={{ height: 3, background: plan.isActive ? `linear-gradient(90deg, ${Colors.primary}, ${Colors.primaryLight})` : Colors.error }} />

                  {/* Card header */}
                  <Box sx={{ p: 3, pb: 2 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box>
                        {badge && (
                          <Box sx={{ display: "inline-block", mb: 1, px: 1.25, py: 0.25, borderRadius: 1, background: badge.bg, border: `1px solid ${badge.border}` }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, color: badge.text }}>{plan.badge}</Typography>
                          </Box>
                        )}
                        <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.2 }}>
                          {plan.name || plan.id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: Colors.textMuted }}>ID: {plan.id} · Orden: {plan.order}</Typography>
                      </Box>
                      <Chip
                        label={plan.isActive ? "Activo" : "Inactivo"}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 700, flexShrink: 0,
                          backgroundColor: plan.isActive ? "rgba(52,199,89,0.12)" : "rgba(255,69,58,0.12)",
                          color: plan.isActive ? Colors.success : Colors.error,
                          border: `1px solid ${plan.isActive ? Colors.success + "44" : Colors.error + "44"}`,
                        }}
                      />
                    </Stack>

                    {/* Price */}
                    <Box sx={{ mt: 2.5, mb: 0.5 }}>
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography sx={{ fontSize: 11, color: Colors.textSecondary, fontWeight: 600, letterSpacing: 1 }}>USD</Typography>
                        <Typography sx={{ fontSize: 38, fontWeight: 900, color: Colors.textPrimary, lineHeight: 1 }}>
                          {(plan.price || 0).toLocaleString()}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: Colors.textSecondary }}>alta</Typography>
                      </Stack>
                      {plan.renewalPrice != null && (
                        <Typography sx={{ fontSize: 12, color: Colors.textMuted, mt: 0.5 }}>
                          Renovación: <strong style={{ color: Colors.textSecondary }}>${(plan.renewalPrice || 0).toLocaleString()}/mes</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: Colors.borderSecondary }} />

                  {/* Features */}
                  <Box sx={{ p: 3, pt: 2, flex: 1 }}>
                    {features.length === 0 ? (
                      <Typography variant="caption" sx={{ color: Colors.textMuted, fontStyle: "italic" }}>Sin características definidas</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {features.map((f, i) => (
                          <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                            <Box sx={{ mt: 0.25, color: Colors.success, fontSize: 16, flexShrink: 0 }}><MdCheck /></Box>
                            <Typography variant="body2" sx={{ color: Colors.textSecondary, fontSize: 13 }}>{f}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Actions */}
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Divider sx={{ borderColor: Colors.borderSecondary, mb: 2 }} />
                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<MdEdit />}
                        onClick={() => openEditor(plan)}
                        sx={{ textTransform: "none", borderRadius: 2, backgroundColor: Colors.primary, color: "#fff", fontWeight: 600, "&:hover": { backgroundColor: Colors.primaryDark } }}
                      >
                        Editar
                      </Button>
                      <Tooltip title="Eliminar plan">
                        <IconButton
                          onClick={() => setDeleteId(plan.id)}
                          sx={{ border: `1px solid ${Colors.error + "44"}`, color: Colors.error, borderRadius: 2, "&:hover": { backgroundColor: Colors.error + "18" } }}
                        >
                          <MdDelete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Edit Drawer ── */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} PaperProps={drawerPaper}>
        <Box sx={{ width: { xs: "100vw", sm: 440 }, p: 3, height: "100%", overflowY: "auto" }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
                {draft?.isNew ? "Nuevo plan" : "Editar plan"}
              </Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
                Los cambios se reflejan en la landing al instante.
              </Typography>
            </Box>

            {saveError && (
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: Colors.error + "18", border: `1px solid ${Colors.error + "44"}` }}>
                <Typography variant="body2" sx={{ color: Colors.error }}>{saveError}</Typography>
              </Box>
            )}

            <TextField label="Nombre del plan" value={draft?.name || ""} onChange={(e) => handleChange("name", e.target.value)} fullWidth placeholder="NUCORE ACCESS" sx={inputSx} />
            {draft?.isNew && (
              <Typography variant="caption" sx={{ color: Colors.textMuted, mt: -1.5, display: "block" }}>
                El ID se genera del nombre. Una vez creado no cambia.
              </Typography>
            )}
            <Stack direction="row" spacing={1.5}>
              <TextField label="Precio inicial (USD)" type="number" value={draft?.price ?? ""} onChange={(e) => handleChange("price", e.target.value)} fullWidth sx={inputSx} helperText="Membresía de alta" />
              <TextField label="Renovación (USD/mes)" type="number" value={draft?.renewalPrice ?? ""} onChange={(e) => handleChange("renewalPrice", e.target.value)} fullWidth sx={inputSx} helperText="Ej: 50" />
            </Stack>
            <TextField label="Orden de aparición" type="number" value={draft?.order ?? ""} onChange={(e) => handleChange("order", e.target.value)} fullWidth helperText="0 = primero a la izquierda" sx={inputSx} />

            <TextField
              select
              label="Badge"
              value={draft?.badge || ""}
              onChange={(e) => handleChange("badge", e.target.value)}
              fullWidth
              SelectProps={{ native: true }}
              sx={inputSx}
            >
              {BADGE_OPTIONS.map((b) => <option key={b} value={b}>{b || "(ninguno)"}</option>)}
            </TextField>

            <FormControlLabel
              control={<Switch checked={Boolean(draft?.isActive)} onChange={(e) => handleChange("isActive", e.target.checked)} sx={{ "& .MuiSwitch-track": { backgroundColor: Colors.borderSecondary } }} />}
              label={<Typography sx={{ color: Colors.textSecondary, fontSize: 14 }}>Visible en landing</Typography>}
            />

            <Divider sx={{ borderColor: Colors.borderSecondary }} />

            <Box>
              <Typography variant="subtitle2" sx={{ color: Colors.textSecondary, mb: 1 }}>Características (una por línea)</Typography>
              <TextField
                value={draft?.features || ""}
                onChange={(e) => handleChange("features", e.target.value)}
                fullWidth
                multiline
                minRows={6}
                placeholder={"Todo lo del plan ACCESS\nSeñales Forex y Futuros\n..."}
                sx={inputSx}
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button variant="outlined" onClick={closeDrawer} disabled={saving} sx={{ textTransform: "none", borderRadius: 2, borderColor: Colors.borderSecondary, color: Colors.textSecondary, flex: 1 }}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: "none", borderRadius: 2, backgroundColor: Colors.primary, color: "#fff", flex: 2, "&:hover": { backgroundColor: Colors.primaryDark } }}>
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      {/* ── Delete confirm ── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, borderRadius: 3 } }}>
        <DialogTitle sx={{ color: Colors.textPrimary }}>Eliminar plan</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: Colors.textSecondary }}>¿Eliminar el plan <strong style={{ color: Colors.textPrimary }}>{deleteId}</strong>? Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ textTransform: "none", color: Colors.textSecondary }}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" sx={{ textTransform: "none", backgroundColor: Colors.error, color: "#fff", borderRadius: 2, "&:hover": { backgroundColor: "#cc2e25" } }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── App Privileges ────────────────────────────────────────────────────────────

function AppPrivilegesTab() {
  const [plans,      setPlans]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft,      setDraft]      = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState("");

  useEffect(() => {
    let active = true;
    const unsubscribe = db.collection("products").onSnapshot(
      async (snapshot) => {
        try {
          const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          const enriched = await Promise.all(
            items.map(async (plan) => {
              try {
                const cs = await db.collection("products").doc(plan.id).collection("privileges").doc("contentSettings").get();
                return { ...plan, contentSettings: cs.exists ? cs.data() : null };
              } catch { return { ...plan, contentSettings: null }; }
            })
          );
          if (!active) return;
          setPlans(enriched);
          setLoading(false);
        } catch {
          if (!active) return;
          setError("No se pudieron cargar los planes.");
          setLoading(false);
        }
      },
      () => { if (!active) return; setError("No se pudieron cargar los planes."); setLoading(false); }
    );
    return () => { active = false; unsubscribe(); };
  }, []);

  const sortedPlans = useMemo(() =>
    [...plans].sort((a, b) => (a.name || a.id || "").localeCompare(b.name || b.id || "")),
    [plans]
  );

  const openEditor = (plan) => {
    setSaveError("");
    setDraft({
      id:               plan.id,
      name:             plan.name || "",
      initialPrice:     plan.initialPrice ?? "",
      monthlyPrice:     plan.monthlyPrice ?? "",
      accountFunds:     plan.accountFunds ?? "",
      isActive:         Boolean(plan.isActive),
      benefits:         Array.isArray(plan.benefits) ? plan.benefits.join("\n") : "",
      academyActive:    Boolean(plan.academyActive),
      communityActive:  Boolean(plan.communityActive),
      schedulesActive:  Boolean(plan.schedulesActive),
      signalsActive:    Boolean(plan.signalsActive),
      tradingActive:    Boolean(plan.tradingActive),
    });
    setEditorOpen(true);
  };

  const closeEditor = () => { if (saving) return; setEditorOpen(false); setDraft(null); };
  const handleDraftChange = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!draft?.id) return;
    setSaving(true);
    setSaveError("");
    try {
      await db.collection("products").doc(draft.id).update({
        name:             draft.name.trim(),
        initialPrice:     Number(draft.initialPrice) || 0,
        monthlyPrice:     Number(draft.monthlyPrice) || 0,
        accountFunds:     Number(draft.accountFunds) || 0,
        isActive:         Boolean(draft.isActive),
        benefits:         draft.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
        academyActive:    Boolean(draft.academyActive),
        communityActive:  Boolean(draft.communityActive),
        schedulesActive:  Boolean(draft.schedulesActive),
        signalsActive:    Boolean(draft.signalsActive),
        tradingActive:    Boolean(draft.tradingActive),
      });
      setEditorOpen(false);
      setDraft(null);
    } catch {
      setSaveError("No se pudo guardar el plan.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: Colors.primary }} /></Box>;
  if (error)   return <Typography color="error">{error}</Typography>;
  if (!sortedPlans.length) return <Typography sx={{ color: Colors.textSecondary }}>Sin planes en `products`.</Typography>;

  return (
    <>
      <Grid container spacing={3}>
        {sortedPlans.map((plan) => (
          <Grid item xs={12} md={6} xl={4} key={plan.id}>
            <Paper
              sx={{
                borderRadius: 3,
                backgroundColor: Colors.bgSecondary,
                border: `1px solid ${Colors.borderSecondary}`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: `0 12px 40px rgba(0,0,0,0.4)` },
              }}
            >
              {/* Status bar */}
              <Box sx={{ height: 3, background: plan.isActive ? `linear-gradient(90deg, ${Colors.success}, ${Colors.successLight})` : Colors.error }} />

              {/* Header */}
              <Box sx={{ p: 3, pb: 2 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>{plan.name || plan.id}</Typography>
                    <Typography variant="caption" sx={{ color: Colors.textMuted }}>ID: {plan.id}</Typography>
                  </Box>
                  <Chip
                    label={plan.isActive ? "Activo" : "Inactivo"}
                    size="small"
                    sx={{
                      fontSize: 10, fontWeight: 700,
                      backgroundColor: plan.isActive ? "rgba(52,199,89,0.12)" : "rgba(255,69,58,0.12)",
                      color: plan.isActive ? Colors.success : Colors.error,
                      border: `1px solid ${plan.isActive ? Colors.success + "44" : Colors.error + "44"}`,
                    }}
                  />
                </Stack>

                {/* Pricing row */}
                <Stack direction="row" spacing={0} sx={{ mt: 2.5, borderRadius: 2, overflow: "hidden", border: `1px solid ${Colors.borderSecondary}` }}>
                  {[
                    { label: "INICIAL", value: `$${plan.initialPrice ?? "—"}` },
                    { label: "MENSUAL", value: `$${plan.monthlyPrice ?? "—"}` },
                    { label: "FONDOS",  value: `$${plan.accountFunds ?? "—"}` },
                  ].map((item, i) => (
                    <Box key={item.label} sx={{ flex: 1, p: 1.5, textAlign: "center", borderLeft: i > 0 ? `1px solid ${Colors.borderSecondary}` : "none", backgroundColor: Colors.bgTertiary }}>
                      <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: Colors.textMuted, mb: 0.5 }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.textPrimary }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider sx={{ borderColor: Colors.borderSecondary }} />

              {/* Module toggles */}
              <Box sx={{ p: 3, pt: 2, flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: Colors.textMuted, mb: 1.5 }}>MÓDULOS</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {MODULES.map((m) => {
                    const on = Boolean(plan[m.key]);
                    return (
                      <Stack
                        key={m.key}
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{
                          px: 1.25, py: 0.6,
                          borderRadius: "20px",
                          border: `1px solid ${on ? Colors.primary + "55" : Colors.borderSecondary}`,
                          backgroundColor: on ? Colors.primary + "14" : "transparent",
                        }}
                      >
                        <Box sx={{ fontSize: 14, color: on ? Colors.primaryLight : Colors.textMuted, display: "flex", alignItems: "center" }}>{m.icon}</Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: on ? Colors.primaryLight : Colors.textMuted }}>{m.label}</Typography>
                        <Box sx={{ fontSize: 12, color: on ? Colors.success : Colors.error, display: "flex", alignItems: "center" }}>
                          {on ? <MdCheck /> : <MdClose />}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>

                {/* Benefits preview */}
                {Array.isArray(plan.benefits) && plan.benefits.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: Colors.textMuted, mb: 1 }}>BENEFICIOS</Typography>
                    <Stack spacing={0.5}>
                      {plan.benefits.slice(0, 3).map((b, i) => (
                        <Stack key={i} direction="row" spacing={0.75} alignItems="center">
                          <Box sx={{ fontSize: 12, color: Colors.success, flexShrink: 0 }}><MdCheck /></Box>
                          <Typography sx={{ fontSize: 12, color: Colors.textSecondary }}>{b}</Typography>
                        </Stack>
                      ))}
                      {plan.benefits.length > 3 && (
                        <Typography sx={{ fontSize: 11, color: Colors.textMuted, pl: 2.5 }}>+{plan.benefits.length - 3} más</Typography>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Action */}
              <Box sx={{ p: 2, pt: 0 }}>
                <Divider sx={{ borderColor: Colors.borderSecondary, mb: 2 }} />
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<MdEdit />}
                  onClick={() => openEditor(plan)}
                  sx={{ textTransform: "none", borderRadius: 2, backgroundColor: Colors.primary, color: "#fff", fontWeight: 600, "&:hover": { backgroundColor: Colors.primaryDark } }}
                >
                  Editar privilegios
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Edit Drawer ── */}
      <Drawer anchor="right" open={editorOpen} onClose={closeEditor} PaperProps={drawerPaper}>
        <Box sx={{ width: { xs: "100vw", sm: 440 }, p: 3, height: "100%", overflowY: "auto" }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Editar plan</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
                {draft?.id} — Firestore: <code style={{ color: Colors.textMuted, fontSize: 11 }}>`products`</code>
              </Typography>
            </Box>

            {saveError && (
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: Colors.error + "18", border: `1px solid ${Colors.error + "44"}` }}>
                <Typography variant="body2" sx={{ color: Colors.error }}>{saveError}</Typography>
              </Box>
            )}

            <TextField label="Nombre" value={draft?.name || ""} onChange={(e) => handleDraftChange("name", e.target.value)} fullWidth sx={inputSx} />

            <Stack direction="row" spacing={1.5}>
              <TextField label="Precio inicial" type="number" value={draft?.initialPrice ?? ""} onChange={(e) => handleDraftChange("initialPrice", e.target.value)} fullWidth sx={inputSx} />
              <TextField label="Precio mensual" type="number" value={draft?.monthlyPrice ?? ""} onChange={(e) => handleDraftChange("monthlyPrice", e.target.value)} fullWidth sx={inputSx} />
            </Stack>
            <TextField label="Fondos de cuenta" type="number" value={draft?.accountFunds ?? ""} onChange={(e) => handleDraftChange("accountFunds", e.target.value)} fullWidth sx={inputSx} />

            <FormControlLabel
              control={<Switch checked={Boolean(draft?.isActive)} onChange={(e) => handleDraftChange("isActive", e.target.checked)} />}
              label={<Typography sx={{ color: Colors.textSecondary, fontSize: 14 }}>Plan activo</Typography>}
            />

            <Divider sx={{ borderColor: Colors.borderSecondary }} />

            <Box>
              <Typography variant="subtitle2" sx={{ color: Colors.textSecondary, mb: 1.5 }}>Módulos habilitados</Typography>
              <Stack spacing={1}>
                {MODULES.map((m) => (
                  <FormControlLabel
                    key={m.key}
                    control={
                      <Switch
                        checked={Boolean(draft?.[m.key])}
                        onChange={(e) => handleDraftChange(m.key, e.target.checked)}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ fontSize: 18, color: draft?.[m.key] ? Colors.primaryLight : Colors.textMuted, display: "flex" }}>{m.icon}</Box>
                        <Typography sx={{ color: Colors.textSecondary, fontSize: 14 }}>{m.label}</Typography>
                      </Stack>
                    }
                  />
                ))}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: Colors.borderSecondary }} />

            <Box>
              <Typography variant="subtitle2" sx={{ color: Colors.textSecondary, mb: 1 }}>Beneficios (uno por línea)</Typography>
              <TextField value={draft?.benefits || ""} onChange={(e) => handleDraftChange("benefits", e.target.value)} fullWidth multiline minRows={4} sx={inputSx} />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button variant="outlined" onClick={closeEditor} disabled={saving} sx={{ textTransform: "none", borderRadius: 2, borderColor: Colors.borderSecondary, color: Colors.textSecondary, flex: 1 }}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: "none", borderRadius: 2, backgroundColor: Colors.primary, color: "#fff", flex: 2, "&:hover": { backgroundColor: Colors.primaryDark } }}>
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────

export default function PlansView() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">
        {/* Page header */}
        <Paper
          sx={{
            mb: 3, p: 2.5, borderRadius: 3,
            background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`,
            border: `1px solid ${Colors.borderSecondary}`,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Planes y Privilegios</Typography>
            <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
              {tab === 0 ? "Planes visibles en la landing page — colección `plans`" : "Privilegios de módulos por plan — colección `products`"}
            </Typography>
          </Box>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { color: Colors.textSecondary, textTransform: "none", fontWeight: 600, minHeight: 40, fontSize: 13 },
              "& .Mui-selected": { color: Colors.textPrimary },
              "& .MuiTabs-indicator": { backgroundColor: Colors.primary },
            }}
          >
            <Tab label="Planes de Venta" />
            <Tab label="Privilegios de App" />
          </Tabs>
        </Paper>

        {tab === 0 && <LandingPlansTab />}
        {tab === 1 && <AppPrivilegesTab />}
      </Container>
    </Box>
  );
}
